import { createSelector } from 'reselect'
import { memoize } from 'utils/function'
import { isEmpty } from 'utils/object'

const UNSAVED_KEY = 'add'

type Item = Record<string, unknown>

// orm slices hold untyped API records
/* eslint-disable @typescript-eslint/no-explicit-any */
type ModelItems = Record<string, any>

export type OrmModel = {
  count?: number
  items?: ModelItems
  order?: string[]
  meta?: Record<string, any>
  [key: string]: unknown
}
/* eslint-enable @typescript-eslint/no-explicit-any */

type SelectableItem = {
  id?: unknown
  label?: string
  name?: string
  title?: string
  __status?: string
}

// SLICE SELECTORS
// getModelSlice :: str -> state -> Model
export const getModelSlice = model => state => state?.orm?.[model]

export const getOrm = state => state?.orm

// ids of all model entries in store
// getItemIds :: transformer -> sorter -> [{k:v}] -> [k]
const getItemIds = (transformer, sorter) => items => {
  const kept = items.filter(Boolean).filter(item => item.__status !== 'deleted')
  return sorter(transformer(kept))
    .map(item => item.id)
    .map(id => (typeof id === 'number' ? String(id) : id))
}

// get react-select options for model
const getSelectOptions = (items: Record<string, SelectableItem | null>) =>
  Object.values(items)
    .filter(item => item?.__status !== 'deleted')
    .filter((item): item is SelectableItem => Boolean(item))
    .map(({ id, label, name, title }) => ({
      value: id,
      label: label || name || title,
    }))

// ids of entries selected in the datatable
// getSelectedItemIds :: Selected -> [pk]
const getSelectedItemIds = selected =>
  Object.keys(selected).filter(pk => selected[pk])

// mergeDirtyItem :: {props, __dirty:{props}} -> {props, isDirty:bool}
// Combine pristine and dirty props from model entry in store
const mergeDirtyItem = ({ __dirty: dirty = {}, ...props }: Item): Item =>
  isEmpty(dirty)
    ? { ...props, isDirty: false }
    : { ...props, ...(dirty as Item), isDirty: true }

const NOT_OK_STATUSES: unknown[] = ['', 'fetching', 'deleted', 'error']

const isOk = (item: Item): Item => ({
  ...item,
  ok: item.pk === UNSAVED_KEY || !NOT_OK_STATUSES.includes(item.__status),
})

// modelSelectors :: str -> {str: selector}
export const modelSelectors = memoize(sliceName => {
  // One default per model, so that a missing slice keeps the same reference
  const noSelection = {}
  const noItems: ModelItems = {}
  const noItem: Item = {}

  const getModel: (state: unknown) => OrmModel | undefined =
    getModelSlice(sliceName)
  const selected = createSelector(
    getModel,
    model => model?.meta?.selected ?? noSelection,
  )
  const items = createSelector(
    (state: unknown): ModelItems => getModel(state)?.items ?? noItems,
    ({ [UNSAVED_KEY]: _unsaved, ...saved }: ModelItems) => saved,
  )
  return {
    getModel,
    getLoaded: createSelector(
      getModel,
      model => model != null && Object.hasOwn(model, 'items'),
    ),
    getItems: items,
    getItemIds: (transformer: any = xs => xs, sorter: any = xs => xs) =>
      createSelector(items, saved =>
        getItemIds(transformer, sorter)(Object.values(saved)),
      ),
    getOptions: createSelector(items, getSelectOptions),
    getSelected: selected,
    getSelectedItemIds: createSelector(selected, getSelectedItemIds),
    getItemSelected: (state, { pk }) => selected(state)[pk] ?? false,
    getDirty: createSelector(
      (state: unknown, { pk }: { pk: string | number }): Item =>
        getModel(state)?.items?.[pk] ?? noItem,
      item => item.__dirty,
    ),
    getItem: (id?: any) =>
      createSelector(
        (state: any, { pk = id }: any = {}): Item =>
          getModel(state)?.items?.[pk] ?? noItem,
        (item): ModelItems => {
          const merged = isOk(mergeDirtyItem(item))
          return merged.id === UNSAVED_KEY
            ? { ...merged, unsaved: true }
            : merged
        },
      ),
    getItemValue: (id?: any, key?: any) =>
      createSelector(
        (state: any, { pk = id, name = key }: any = {}) => {
          const item = getModel(state)?.items?.[pk] ?? {}
          const dirty = item.__dirty
          return dirty != null && Object.hasOwn(dirty, name)
            ? dirty[name]
            : item[name]
        },
        value => ({ value }),
      ),
  }
})
